import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import SearchableSelect from '@/components/common/SearchableSelect';
import type { LocationDto, ZoneDto } from '@/types/organization';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    zoneId: z.string().nullable(),
    locationType: z.enum(['Rack', 'Bin']),
    storageType: z.enum(['Pallet', 'Box', 'Loose']),
    rowNo: z.string().nullable(),
    columnNo: z.string().nullable(),
    maxWeight: z.coerce.number().nullable(),
    maxVolume: z.coerce.number().nullable(),
  });
}

export type LocationFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface LocationFormDialogProps {
  open: boolean;
  location: LocationDto | null;
  zones: ZoneDto[];
  onClose: () => void;
  onSubmit: (values: LocationFormValues) => void;
  loading?: boolean;
}

export default function LocationFormDialog({ open, location, zones, onClose, onSubmit, loading }: LocationFormDialogProps) {
  const { t } = useTranslation('warehouseMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<LocationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: '', zoneId: null, locationType: 'Bin', storageType: 'Box', rowNo: null, columnNo: null, maxWeight: null, maxVolume: null,
    },
  });

  useEffect(() => {
    if (open) {
      reset(
        location
          ? {
              code: location.code, zoneId: location.zoneId, locationType: location.locationType, storageType: location.storageType,
              rowNo: location.rowNo, columnNo: location.columnNo, maxWeight: location.maxWeight, maxVolume: location.maxVolume,
            }
          : { code: '', zoneId: null, locationType: 'Bin', storageType: 'Box', rowNo: null, columnNo: null, maxWeight: null, maxVolume: null },
      );
    }
  }, [open, location, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{location ? t('table.code') : t('addLocation')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={2}>
          <GridItem xs={1}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField {...field} label={t('form.code')} fullWidth sx={{ mt: 1 }} error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="zoneId"
              control={control}
              render={({ field }) => (
                <SearchableSelect
                  label={t('form.zone')} value={field.value ?? ''} onChange={field.onChange} fullWidth sx={{ mt: 1 }}
                  options={[{ value: '', label: t('form.noZone') }, ...zones.map((z) => ({ value: z.id, label: z.name }))]}
                />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="locationType"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.locationType')} fullWidth>
                  <MenuItem value="Rack">Rack</MenuItem>
                  <MenuItem value="Bin">Bin</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="storageType"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label={t('form.storageType')} fullWidth>
                  <MenuItem value="Pallet">Pallet</MenuItem>
                  <MenuItem value="Box">Box</MenuItem>
                  <MenuItem value="Loose">Loose</MenuItem>
                </TextField>
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="rowNo" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.rowNo')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller name="columnNo" control={control} render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.columnNo')} fullWidth />} />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="maxWeight"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} type="number" label={t('form.maxWeight')} fullWidth />}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="maxVolume"
              control={control}
              render={({ field }) => <TextField {...field} value={field.value ?? ''} type="number" label={t('form.maxVolume')} fullWidth />}
            />
          </GridItem>
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
